import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { controlPlane } from '../lib/client'
import { rfStore } from '../lib/rfstore'
import { useI18n } from '../lib/i18n'

/** Últimos segundos antes do corte automático do servidor em que o botão
 * avisa — cor de perigo, vibração extra, barra ficando vermelha. */
const AVISO_SEGUNDOS = 15

/**
 * Botão de PTT.
 *
 * Requisitos de docs/05-audio-dsp-wasm.md §5 e docs/06-frontend-ux-mobile.md §3:
 *  - alvo mínimo de 96 px (é o controle mais usado do app)
 *  - pointer capture: continua transmitindo se o dedo escorregar
 *  - modo toggle além de segurar, por acessibilidade — obrigatório, não opcional
 *  - vibração ao entrar e ao sair
 */
export function PTT({ powerW = 100 }: { powerW?: number }) {
  const { t } = useI18n()
  const [toggleMode, setToggleMode] = useState(false)
  const [restam, setRestam] = useState<number | null>(null)
  // Marca se já vibrou o aviso desta transmissão — sem isto, vibraria a
  // cada segundo inteiro dentro da janela de aviso, não só uma vez.
  const avisadoRef = useRef(false)

  const state = useSyncExternalStore(rfStore.subscribe, () => rfStore.snapshot())
  const { transmitting, channel, connected, toning } = state

  const canTransmit = connected && (channel?.can_transmit ?? false)

  const start = useCallback(() => {
    if (!canTransmit) return
    navigator.vibrate?.(12)
    controlPlane.pttDown(powerW)
  }, [canTransmit, powerW])

  const stop = useCallback(() => {
    navigator.vibrate?.(8)
    controlPlane.pttUp()
  }, [])

  // Contador regressivo até o corte automático do servidor
  // (Hub.MaxTXSeconds, espelhado em TXGranted.max_seconds). Um segundo de
  // resolução é de sobra — não é o caminho quente do rádio, é só o
  // operador saber quanto tempo ainda tem.
  useEffect(() => {
    if (!transmitting) {
      setRestam(null)
      avisadoRef.current = false
      return
    }
    const tick = () => {
      const { txMaxSeconds, txStartedAtMs } = rfStore.snapshot()
      if (txMaxSeconds === null || txStartedAtMs === null) {
        setRestam(null)
        return
      }
      const passado = (Date.now() - txStartedAtMs) / 1000
      const falta = Math.max(0, Math.ceil(txMaxSeconds - passado))
      setRestam(falta)
      if (falta <= AVISO_SEGUNDOS && !avisadoRef.current) {
        avisadoRef.current = true
        navigator.vibrate?.([30, 40, 30])
      }
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [transmitting])

  // Barra de espaço no desktop.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat && !toggleMode) {
        e.preventDefault()
        start()
      }
    }
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !toggleMode) {
        e.preventDefault()
        stop()
      }
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [start, stop, toggleMode])

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    if (toggleMode) {
      transmitting ? stop() : start()
    } else {
      start()
    }
  }

  // O dedo soltou: É a informação, não uma confirmação do servidor que
  // demora um round-trip pra chegar (tx_granted é assíncrono). Checar
  // `transmitting` aqui é o bug do toque duplo — num toque rápido o dedo já
  // solta ANTES de `transmitting` virar true, o `if` não entra, e a
  // transmissão fica presa até o corte de 180s do servidor. Mandar ptt_up
  // sem uma transmissão confirmada ainda é seguro e correto: Hub.PTTUp já é
  // no-op se não havia nada em curso, e a ordem das duas mensagens no mesmo
  // WebSocket garante que o servidor processa ptt_down antes de ptt_up.
  const handlePointerUp = () => {
    if (!toggleMode) stop()
  }

  const emAviso = restam !== null && restam <= AVISO_SEGUNDOS
  const progresso =
    restam !== null && state.txMaxSeconds ? Math.max(0, restam / state.txMaxSeconds) * 100 : 100

  return (
    <div className="ptt-wrap">
      <button
        className={`ptt ${transmitting ? 'tx' : ''} ${toning ? 'bipe' : ''} ${emAviso ? 'aviso' : ''}`}
        disabled={!canTransmit}
        aria-pressed={transmitting}
        aria-label={transmitting ? t.ptt.transmitindoSolte : t.ptt.segureParaFalar}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onLostPointerCapture={handlePointerUp}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Barra que encolhe até o corte automático — mesma informação do
            número embaixo, só que perceptível com o canto do olho, sem
            precisar ler. */}
        {transmitting && restam !== null && (
          <div className="ptt-barra" style={{ width: `${progresso}%` }} />
        )}
        {/* BIPE é um estado de verdade, não enfeite: com bipe de início o
            microfone só abre depois do tom, e sem isto a pessoa fala por cima
            dele e perde a primeira palavra toda vez. */}
        <span className="ptt-label">
          {toning ? t.ptt.bipe : transmitting ? t.ptt.noAr : t.ptt.pttLabel}
        </span>
        {/* O motivo da recusa NÃO se repete aqui: ele já está logo acima, no
            dock, em cima do botão. Dizer duas vezes a mesma coisa na mesma
            tela é ruído, não reforço. */}
        <small>
          {toning
            ? t.ptt.aguardeBipe
            : !connected
            ? t.ptt.desconectado
            : !canTransmit
              ? t.ptt.semPrivilegio
              : transmitting
                ? `${toggleMode ? t.ptt.toqueParaParar : t.ptt.solteParaOuvir}${
                    restam !== null ? ` · ${restam}s` : ''
                  }`
                : toggleMode
                  ? t.ptt.toqueParaFalar
                  : t.ptt.segureParaFalar2}
        </small>
      </button>

      <button
        className="ptt-modo"
        aria-pressed={toggleMode}
        onClick={() => setToggleMode((v) => !v)}
        title={t.ptt.alternarTitle}
      >
        {toggleMode ? t.ptt.toque : t.ptt.segurar}
      </button>
    </div>
  )
}
